import type { CreatorActivity } from "@unbox/api";
import { differenceInDays, format } from "date-fns";

/**
 * Format a date for display in activity day section headers (right panel)
 * Format: "Monday - 15th Jan 2025"
 */
export const formatActivityDayHeader = (date: Date): string => {
  const dayName = format(date, "EEEE"); // "Monday"
  const dayMonth = format(date, "do MMM"); // "15th Jan"
  const year = format(date, "yyyy"); // "2025"

  return `${dayName} - ${dayMonth} ${year}`;
};

/**
 * Format a date for display in the left panel (creator list)
 * Logic:
 * - "1 day ago" (Today)
 * - "2 days ago" (Yesterday)
 * - "3 days ago", "4 days ago", etc. (up to 7 days)
 * - "8th Feb" (absolute dates from 8 days onwards, current year)
 * - "2025-01-07" (absolute dates from previous/future years - YYYY-MM-DD)
 */
export const formatActivityDate = (date: Date): string => {
  const now = new Date();
  const daysAgo = differenceInDays(now, date);

  // For dates within the last 7 days, use relative format
  if (daysAgo === 0) {
    return "1 day ago";
  }

  if (daysAgo === 1) {
    return "2 days ago";
  }

  if (daysAgo >= 2 && daysAgo < 7) {
    return `${daysAgo + 1} days ago`;
  }

  // For dates 7 days or older, use absolute format
  // Use numeric format with year if it's not the current year
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  if (isCurrentYear) {
    return format(date, "do MMM"); // "8th Feb"
  }
  return format(date, "yyyy-MM-dd"); // "2025-01-07"
};

/**
 * Group activities by day
 * Returns a map of date strings to activities
 * Uses formatActivityDayHeader for the day section headers
 */
export const groupActivitiesByDay = (
  activities: CreatorActivity[],
): Map<string, { date: Date; dateLabel: string; activities: CreatorActivity[] }> => {
  const grouped = new Map<string, { date: Date; dateLabel: string; activities: CreatorActivity[] }>();

  for (const activity of activities) {
    const activityDate = new Date(activity.timestamp);
    // Use date string as key (YYYY-MM-DD)
    const dateKey = format(activityDate, "yyyy-MM-dd");

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, {
        date: activityDate,
        dateLabel: formatActivityDayHeader(activityDate),
        activities: [],
      });
    }

    const group = grouped.get(dateKey);
    if (group) {
      group.activities.push(activity);
    }
  }

  return grouped;
};
